import React, { useEffect, useRef, useState } from 'react';
import { Typography, Box, Grid, TextField, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Styles from '../Config/Styles';
import { storage } from '../Config/Storage';
import { sendotp, verifyotp } from '../Redux/Login/action';
import { getdraftOrders } from '../Redux/Mycart/action';
import ButtonBox from './Button';
import Backbutton from './Backbutton';

const VerifyOtp = ({ width, setComponent, setLoading }) => {
  // Redux state
  const { orders } = useSelector((state) => state?.CartData) || [];

  // Local state
  const [otp, setOtp] = useState(new Array(4).fill('')); // Array to store 4-digit OTP
  const [disabled, setDisabled] = useState(true); // Disabled state for the "Verify" button

  // Refs for OTP input fields
  const inputRefs = useRef([]);

  // Redux hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Retrieve phone number and ID from local storage
  const number = storage.fetch.phonenumber();
  const id = storage.fetch.phone();
  const phoneMasked = number.slice(-2); // Masked phone number for display

  // Fetch draft orders on initial render if not already loaded
  useEffect(() => {
    if (!orders?.length) {
      const session = sessionStorage.getItem("session");
      dispatch(getdraftOrders({ session }));
    }
  }, []);

  // Update "Verify" button state based on OTP input
  useEffect(() => {
    setDisabled(otp.some((digit) => digit === '')); // Disabled if any OTP field is empty
  }, [otp, orders]);

  // Handle OTP verification on button click
  const handleVerifyClick = () => {
    setLoading(true);
    const data = { otp: otp.join(''), id };
    dispatch(verifyotp(data, orders, setComponent, setLoading, navigate));
  };

  // Handle input change for OTP fields
  const handleInputChange = (e, index) => {
    const value = e.target.value;
    if (/^[0-9]?$/.test(value)) {
      const updatedOtp = [...otp];
      updatedOtp[index] = value;
      setOtp(updatedOtp);

      // Move focus to the next input field
      if (value && index < otp.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  // Handle key navigation (e.g., Backspace) in OTP fields
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Navigate back to phone input screen
  const handleBackClick = () => {
    setComponent("phone");
  };

  // Resend OTP handler
  const handleResend = () => {
    setLoading(true);
    dispatch(sendotp({ phone: number }, setComponent, setLoading));
  };

  return (
    <Box sx={Styles.middleBoxStyle(width).box2}>
      {/* Back Button */}
      <Backbutton onClick={handleBackClick} />

      {/* OTP Input Section */}
      <Box sx={Styles.middleBoxStyle(width).innerbox}>
        <Typography
          variant="h3"
          sx={{
            ...Styles.middleBoxStyle(width).heading,
            fontSize: { xs: "24px", md: "35px" },
            pb: "15px",
          }}
        >
          Enter Verification Code
        </Typography>
        <Typography variant="h6" sx={Styles.middleBoxStyle().text}>
          We’ve sent the OTP code to +966 _ _ _ _ _ _ _{phoneMasked}
        </Typography>

        <Box sx={Styles.middleBoxStyle(width).box1}>
          <Grid container spacing={2}>
            {/* OTP Input Fields */}
            <Grid item xs={12} sx={Styles.middleBoxStyle(width).grid}>
              <Grid container spacing={2} sx={Styles.middleBoxStyle(width).grid1}>
                {otp.map((_, index) => (
                  <Grid item key={index} sx={Styles.middleBoxStyle(width).innergrid}>
                    <TextField
                      variant="filled"
                      value={otp[index]}
                      onChange={(e) => handleInputChange(e, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      InputProps={{
                        sx: Styles.middleBoxStyle().input,
                      }}
                      inputProps={{
                        maxLength: 1,
                        style: { textAlign: "center", fontSize: "20px" },
                      }}
                      inputRef={(ref) => (inputRefs.current[index] = ref)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} sx={Styles.middleBoxStyle(width).grid}>
              <ButtonBox
                text="Verify"
                type="verify"
                onClick={handleVerifyClick}
                color="black"
                disabled={disabled}
              />
              <Button sx={Styles.middleBoxStyle().resendbtn} onClick={handleResend}>
                Resend OTP
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default VerifyOtp;
